import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client'
import { Alert } from '../components/Alert'
import { BbIcon } from '../components/BbIcon'
import { LoadingState, ErrorState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatCredits, formatMoney } from '../lib/format'

/* Stand-in for the payment gateway's hosted checkout, reachable only when the
   offline gateway is active.

   The real flow leaves the app: the browser goes to Stripe, Stripe collects a
   card, and a webhook comes back. With no Stripe configured there is nowhere to
   go, so this page does the two things that matter — it shows what is being
   bought, and on confirm it has the API write the webhook Stripe would have
   sent. Everything downstream is the production path, untouched.

   Deliberately outside the /app layout: the real checkout is a different origin
   with its own chrome, and rendering it inside the app shell would make the
   demo look like an in-app confirmation rather than a payment. */

interface MockSession {
  sessionId: string
  kind: 'subscription' | 'topup'
  description: string
  amountUsd: number
  credits: number
  planName: string | null
}

export function MockCheckoutPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const sessionId = params.get('session') ?? ''
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const session = useAsync<MockSession>(
    () =>
      sessionId
        ? api.get<MockSession>(`/billing/mock/session/${encodeURIComponent(sessionId)}`)
        : Promise.reject(new ApiError(404, 'MISSING_SESSION', 'No checkout session was given.')),
    [sessionId],
  )

  /**
   * Confirms the payment, then hands the browser back to billing where the
   * result shows. The webhook is processed in the background, so the credits and
   * the subscription can lag a moment behind the redirect.
   */
  const pay = async () => {
    setPaying(true)
    setError(null)
    try {
      await api.post<void>(`/billing/mock/session/${encodeURIComponent(sessionId)}/pay`)
      navigate('/app/account?billing=done', { replace: true })
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not complete the payment.')
      setPaying(false)
    }
  }

  const cancel = () => navigate('/app/account', { replace: true })

  return (
    <div className="bb" style={{ minHeight: '100vh', background: 'var(--bb-surface-bg)', padding: '48px 24px' }}>
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        <div className="bb-welcome-brand" style={{ color: 'var(--bb-primary)', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
          <BbIcon name="chart" />
          BatchBooks
        </div>

        <div
          className="bb-panel-card"
          style={{ marginTop: 20, background: 'var(--bb-surface-lowest)', borderRadius: 12, border: '1px solid var(--bb-line)' }}
        >
          <div className="bb-panel-card-b" style={{ padding: 28 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--bb-primary)', marginBottom: 4 }}>
              {session.data?.kind === 'topup' ? 'Buy credits' : 'Subscribe'}
            </h2>
            <p className="bb-map-help" style={{ marginBottom: 20 }}>
              Offline test checkout — no card is collected and nothing is charged.
            </p>

            {session.loading ? (
              <LoadingState label="Loading checkout…" />
            ) : session.error ? (
              <ErrorState error={session.error} onRetry={session.reload} />
            ) : (
              <>
                <div
                  className="row"
                  style={{
                    padding: '14px 0',
                    borderTop: '1px solid var(--bb-line)',
                    borderBottom: '1px solid var(--bb-line)',
                    alignItems: 'baseline',
                  }}
                >
                  <div>
                    <b className="fs13">
                      {session.data?.planName ?? session.data?.description}
                    </b>
                    <div className="fs12 faint">
                      {session.data?.kind === 'topup'
                        ? `${formatCredits(session.data.credits)} credits`
                        : 'Monthly subscription'}
                    </div>
                  </div>
                  <span className="spacer" />
                  {session.data?.amountUsd ? (
                    <span className="mono fs13">{formatMoney(session.data.amountUsd)}</span>
                  ) : null}
                </div>

                {session.data?.kind === 'subscription' ? (
                  <p className="fs12 faint" style={{ margin: '12px 0 0' }}>
                    Your card would be charged here, and the subscription would start. You will
                    keep the plan until you cancel.
                  </p>
                ) : null}

                {error ? (
                  <Alert tone="err" title="Payment failed" style={{ marginTop: 16 }}>
                    {error}
                  </Alert>
                ) : null}

                <div className="row" style={{ gap: 8, marginTop: 20 }}>
                  <button className="bb-btn bb-btn-quiet" type="button" onClick={cancel} disabled={paying}>
                    Cancel
                  </button>
                  <span className="spacer" />
                  <button
                    className="bb-btn bb-btn-green"
                    type="button"
                    disabled={paying}
                    onClick={() => void pay()}
                  >
                    <BbIcon name="check-circle" />
                    {paying ? 'Completing…' : 'Complete payment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="fs12 faint" style={{ textAlign: 'center', marginTop: 16 }}>
          This screen only exists while the offline payment gateway is active.
        </p>
      </div>
    </div>
  )
}
